import React, { useState, useMemo } from 'react';
import { Table, IconButton, TableHead, TableBody, TableRow, TableCell, TableSortLabel, Box, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditSiteDialogBox from './EditSiteDialogBox';
import DeleteSiteDialogBox from './DeleteSiteDialogBox';

const AllSitesTable = ({ sitesData = [], selectedCompanyId, isSmallScreen, refetch }) => {
    const theme = useTheme();

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [editSite, setEditSite] = useState(null);
    const [deleteSite, setDeleteSite] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    // Handle sort
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const createSortHandler = (property) => (event) => {
        handleRequestSort(property);
    };

    // Process data
    const filteredSitesData = useMemo(() => {
        if (!Array.isArray(sitesData)) {
            return [];
        }

        if (!selectedCompanyId) {
            return sitesData;
        } else {
            return sitesData.filter((site) => site.company._id === selectedCompanyId);
        }
    }, [sitesData, selectedCompanyId]);

    const sortedData = useMemo(() => {
        if (!Array.isArray(filteredSitesData)) {
            return [];
        }

        return [...filteredSitesData].sort((a, b) => {
            if (order === 'asc') {
                return a[orderBy] > b[orderBy] ? 1 : -1;
            } else {
                return b[orderBy] > a[orderBy] ? 1 : -1;
            }
        });
    }, [filteredSitesData, orderBy, order]);

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString();
    };

    // Handle edit
    const handleEdit = (site) => {
        setEditSite(site);
        setOpenEditDialog(true);
    };

    // Handle delete
    const handleDelete = (site) => {
        setDeleteSite(site);
        setOpenDeleteDialog(true);
    };

    return (
        <Box
            backgroundColor={theme.palette.background.paper}
            borderRadius="15px"
            sx={{
                overflowX: isSmallScreen ? 'auto' : 'hidden',
                '&::-webkit-scrollbar': {
                    display: 'none',
                },
                mt: '1rem',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
            }}
        >
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={createSortHandler('name')}>
                                Name
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel active={orderBy === 'company'} direction={orderBy === 'company' ? order : 'asc'} onClick={createSortHandler('company')}>
                                Company
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel active={orderBy === 'location'} direction={orderBy === 'location' ? order : 'asc'} onClick={createSortHandler('location')}>
                                Location
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel active={orderBy === 'enabled'} direction={orderBy === 'enabled' ? order : 'asc'} onClick={createSortHandler('enabled')}>
                                Enabled
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Latitude</TableCell>
                        <TableCell>Longitude</TableCell>
                        <TableCell>Connected MACs</TableCell>
                        <TableCell>
                            <TableSortLabel active={orderBy === 'createdAt'} direction={orderBy === 'createdAt' ? order : 'asc'} onClick={createSortHandler('createdAt')}>
                                Created Date
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel active={orderBy === 'updatedAt'} direction={orderBy === 'updatedAt' ? order : 'asc'} onClick={createSortHandler('updatedAt')}>
                                Updated Date
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedData.map((site) => (
                        <TableRow key={site._id}>
                            <TableCell>{site.name}</TableCell>
                            <TableCell>{site.company?.alternativeName !== 'N/A' ? site.company?.alternativeName : site.company?.name}</TableCell>
                            <TableCell>{site.location}</TableCell>
                            <TableCell>{site.enabled ? 'Yes' : 'No'}</TableCell>
                            <TableCell>{site.latitude}</TableCell>
                            <TableCell>{site.longitude}</TableCell>
                            <TableCell>{site.connectedMACs?.map((mac) => mac.name).join(', ')}</TableCell>
                            <TableCell>{formatDate(site.createdAt)}</TableCell>
                            <TableCell>{formatDate(site.updatedAt)}</TableCell>
                            <TableCell>
                                <IconButton
                                    onClick={() => handleEdit(site)}
                                    sx={{
                                        '&:focus': { outline: 'none' },
                                        '&:hover': { backgroundColor: 'transparent' },
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    <EditIcon color="primary" />
                                </IconButton>
                                <IconButton
                                    onClick={() => handleDelete(site)}
                                    sx={{
                                        '&:focus': { outline: 'none' },
                                        '&:hover': { backgroundColor: 'transparent' },
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    <DeleteIcon color="error" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <EditSiteDialogBox open={openEditDialog} onClose={() => setOpenEditDialog(false)} site={editSite} refetch={refetch} />

            <DeleteSiteDialogBox open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} site={deleteSite} refetch={refetch} />
        </Box>
    );
};

export default AllSitesTable;
